import { Button } from '@/components/ui/button';
import { useApiClient } from '@/lib/api';
import { CheckCircle, ChevronLeft, Loader2, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ReadinessResponse {
    is_ready: boolean;
    missing_items: string[];
}

interface FinishStepProps {
    onBack: () => void;
}

const FinishStep: React.FC<FinishStepProps> = ({ onBack }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [readinessData, setReadinessData] = useState<ReadinessResponse | null>(null);
    const { api } = useApiClient();
    const navigate = useNavigate();

    useEffect(() => {
        const checkReadiness = async () => {
            try {
                const data = await api.checkReadiness();
                setReadinessData(data);
                if (data.is_ready) {
                    setTimeout(() => {
                        navigate('/dashboard');
                    }, 3000); // 3-second delay before redirect
                }
            } catch (error) {
                console.error("Failed to check readiness:", error);
                setReadinessData({ is_ready: false, missing_items: ["There was an error checking your profile's readiness."] });
            } finally {
                setIsLoading(false);
            }
        };

        checkReadiness();
    }, [api, navigate]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8">
                <Loader2 className="h-12 w-12 animate-spin text-interview-blue mb-4" />
                <h2 className="text-2xl font-semibold text-gray-800">Checking your profile...</h2>
            </div>
        );
    }

    if (readinessData?.is_ready) {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8">
                 <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                <h2 className="text-3xl font-bold text-gray-900">All Set!</h2>
                <p className="text-gray-600 mt-2">We're redirecting you in a second...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center text-center p-8">
            <XCircle className="h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">Almost there!</h2>
            <p className="text-gray-600 mt-2 mb-4">We need a bit more information to help you:</p>
            <ul className="list-disc list-inside bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-left w-full max-w-md">
                <li>At least one education, employment, or expanded profile section</li>
            </ul>
            <Button
                onClick={onBack}
                variant="outline"
                className="mt-6"
            >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
            </Button>
        </div>
    );
};

export default FinishStep;
